import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import {
  EventTicketDialogResultEnum,
  EventTicketEntity,
} from 'libs/gb-front/event-tickets/data-access/src/lib/+state/event-tickets.models'
import * as EventTicketsActions from 'libs/gb-front/event-tickets/data-access/src/lib/+state/event-tickets.actions'
import { EventTicketsFacade } from 'libs/gb-front/event-tickets/data-access/src/lib/+state/event-tickets.facade'
import { ActivatedRoute } from '@angular/router'
import { GbFrontCookieService } from 'libs/gb-front/auth/util/src/lib/gb-front-cookie/gb-front-cookie.service'
import { GbVerifyFormComponent } from 'libs/gb-front/auth/feature/verify/src/lib/gb-verify-form/gb-verify-form.component'
import { AuctionFacade } from 'libs/gb-front/auction/data-access/src/lib/+state/auction.facade'
import { GbVerifyIdentityService } from 'libs/gb-front/auth/util/src/lib/gb-verify-identity/gb-verify-identity.service'
import { AuthFacade } from 'libs/gb-front/auth/data-access/src/lib/+state/auth.facade'
import * as AuthActions from 'libs/gb-front/auth/data-access/src/lib/+state/auth.actions'
import { FormUtils } from 'libs/shared/utils/helpers/src/lib/form-utils'
import { GbFrontDialogService } from 'libs/gb-front/shared/util/src/lib/gb-front-dialog-service/gb-front-dialog.service'
import { GbEventTicketGuestStepperComponent } from 'libs/shared/event-tickets/feature/src/lib/gb-event-ticket-guest-stepper/gb-event-ticket-guest-stepper.component'
import { GbEventTicketGuestStepperViewController } from 'libs/shared/event-tickets/feature/src/lib/gb-event-ticket-guest-stepper/gb-event-ticket-guest-stepper-view-controller.service'
import { FormControl } from '@angular/forms'
import {
  EventTicketGuestStepperConfig,
  EventTicketGuestStepperPayload,
} from 'libs/shared/event-tickets/data-access/src/lib/+state/base-event-tickets.models'

@Component({
  selector: 'gb-event-ticket-accept-dialog',
  templateUrl: './gb-event-ticket-accept-dialog.component.html',
  styleUrls: ['./gb-event-ticket-accept-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbEventTicketAcceptDialogComponent implements OnInit, OnDestroy {
  @ViewChild('verifyForm') verifyForm?: GbVerifyFormComponent
  @ViewChild('stepper') stepper?: GbEventTicketGuestStepperComponent

  selectedEventTicket$ = this.eventTicketsFacade.selectedEventTicket$
  selectedEventTicketProfileMismatch$ = this.eventTicketsFacade.selectedEventTicketProfileMismatch$
  selectedStepIndex$ = this.gbEventTicketGuestStepperViewController.selectedStepSubject$
  displaySocialLogin$ = this.auctionFacade.displaySocialLogin$
  identityReady$ = this.gbVerifyIdentityService.identityReady$

  socialLoginSuccessAction = EventTicketsActions.handleEventTicketSocialLogin({ token: this.token })
  showVerifyStep = false
  password = new FormControl('')

  get token(): string {
    return this.route.snapshot.queryParams['token']
  }
  get isAuthenticated(): boolean {
    return this.gbFrontCookieService.isAuthenticated()
  }

  constructor(
    private eventTicketsFacade: EventTicketsFacade,
    private auctionFacade: AuctionFacade,
    private authFacade: AuthFacade,
    private gbEventTicketGuestStepperViewController: GbEventTicketGuestStepperViewController,
    private gbVerifyIdentityService: GbVerifyIdentityService,
    private gbFrontCookieService: GbFrontCookieService,
    private gbFrontDialogService: GbFrontDialogService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.showVerifyStep = !this.isAuthenticated
  }

  ngOnDestroy(): void {
    this.gbVerifyIdentityService.clearIdentity()
  }

  getStepperConfig(eventTicket: EventTicketEntity, selectedStep: number | null): EventTicketGuestStepperConfig {
    return {
      title: 'EVENT_TICKET.ACCEPT_TICKET.HEADER',
      buttonLabel: this.hasCustomFields(eventTicket)
        ? 'EVENT_TICKET.ACCEPT_TICKET.COMPLETE_BUTTON'
        : 'EVENT_TICKET.ACCEPT_TICKET.ACCEPT_BUTTON',
      shouldShowSelfAssign: false,
      shouldPrepopulateForm: true,
      canEditEmail: !this.isAuthenticated,
      emitOnStepSubmit: true,
      shouldShowCancelButton: selectedStep === 0,
      showDynamicStep: this.showVerifyStep,
      dynamicStepLabel: 'EVENT_TICKET_PURCHASES.VERIFY_STEP_LABEL',
      skipLinkLabel: 'EVENT_TICKET.ACCEPT_TICKET.SKIP_LINK',
    }
  }

  hasCustomFields(eventTicket: EventTicketEntity | undefined): boolean {
    return !!eventTicket && eventTicket.ticket_package.custom_fields.length > 0
  }

  onSubmit(
    stepperPayload: EventTicketGuestStepperPayload,
    selectedStep: number | null,
    eventTicket: EventTicketEntity,
    profileMismatch?: boolean | null,
  ): void {
    if (selectedStep === 0) {
      this.onGuestStepComplete(stepperPayload, eventTicket, profileMismatch)
    } else if (selectedStep === 1 && this.showVerifyStep) {
      this.onVerifyStepComplete()
    } else {
      this.onQuestionsStepComplete(stepperPayload)
    }
  }

  onGuestStepComplete(
    stepperPayload: EventTicketGuestStepperPayload,
    eventTicket: EventTicketEntity,
    profileMismatch?: boolean | null,
  ): void {
    this.eventTicketsFacade.dispatch(
      EventTicketsActions.acceptEventTicketInvite({
        profile: { ...stepperPayload.profile, password: this.password.value || undefined },
        token: this.token,
        isAcceptOnBehalf: !!profileMismatch,
        isStepper: true,
        isVerificationRequired:
          stepperPayload.profile?.email?.toLowerCase() !== eventTicket.profile?.email.toLowerCase(),
      }),
    )
  }

  onVerifyStepComplete(): void {
    if (this.isVerifyFormInvalid()) return
    this.authFacade.dispatch(
      AuthActions.verify({
        payload: {
          identity: this.gbVerifyIdentityService.identity,
          verification_code: this.verifyForm?.verificationValue,
        },
        dialog: true,
        onSuccess: () => EventTicketsActions.verifySuccess(),
      }),
    )
  }

  isVerifyFormInvalid(): boolean {
    if (this.verifyForm?.form.invalid) {
      FormUtils.markFieldsAsTouched(this.verifyForm.form)
      this.verifyForm.form.updateValueAndValidity()
      return true
    }
    return false
  }

  onQuestionsStepComplete(stepperPayload: EventTicketGuestStepperPayload): void {
    this.eventTicketsFacade.dispatch(
      EventTicketsActions.editEventTicket({
        payload: { custom_values: stepperPayload.customValues },
        onSuccess: () =>
          this.gbFrontDialogService.closeDialog(
            GbEventTicketAcceptDialogComponent,
            EventTicketDialogResultEnum.Success,
          ),
      }),
    )
  }

  /**
   * Re-assigns ticket to current logged-in user when ticket is assigned to another profile
   */
  onAssignToMe(): void {
    this.eventTicketsFacade.dispatch(
      EventTicketsActions.acceptEventTicketInvite({ token: this.token, isStepper: true }),
    )
    this.stepper!.isSelfAssigned = true
  }

  openEventTicketDeclineDialog(): void {
    this.eventTicketsFacade.dispatch(EventTicketsActions.openEventTicketDeclineDialog())
  }
}
